import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Plus, Trash, Calculator } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const fmt = (n) => `Rs. ${new Intl.NumberFormat('en-NP').format(Math.round(n || 0))}`;

export default function EMIPage() {
  const [emis, setEmis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ loan_name: '', principal: '', interest_rate: '', tenure_months: '', start_date: new Date().toISOString().slice(0, 10) });
  const [calcResult, setCalcResult] = useState(null);
  const [calcForm, setCalcForm] = useState({ principal: '', interest_rate: '', tenure_months: '' });

  const load = () => {
    api.get('/emis').then(r => setEmis(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/emis', {
        ...form,
        principal: parseFloat(form.principal),
        interest_rate: parseFloat(form.interest_rate),
        tenure_months: parseInt(form.tenure_months)
      });
      setDialogOpen(false);
      setForm({ loan_name: '', principal: '', interest_rate: '', tenure_months: '', start_date: new Date().toISOString().slice(0, 10) });
      load();
    } catch {}
  };

  const handleCalc = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/emis/calculate', {
        loan_name: 'calc',
        principal: parseFloat(calcForm.principal),
        interest_rate: parseFloat(calcForm.interest_rate),
        tenure_months: parseInt(calcForm.tenure_months),
        start_date: new Date().toISOString().slice(0, 10)
      });
      setCalcResult(data);
    } catch {}
  };

  const handleDelete = async (id) => {
    await api.delete(`/emis/${id}`);
    load();
  };

  const totalEMI = emis.filter(e => e.status === 'active').reduce((s, e) => s + e.emi_amount, 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="emi-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>EMI Manager</h1>
          <p className="text-[#5C5A56] text-sm mt-1">Calculate and track your loan EMIs</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-emi-btn" className="bg-[#1E3F2D] text-white font-semibold rounded-xl px-5 py-2.5 hover:bg-[#29563E] transition-colors active:scale-95 flex items-center gap-2 text-sm">
              <Plus size={18} weight="bold" /> Add Loan
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#E8E6E1] rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Add New Loan</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Loan Name</label>
                <input data-testid="loan-name-input" type="text" value={form.loan_name} onChange={(e) => setForm({ ...form, loan_name: e.target.value })}
                  className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="Home Loan" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Principal (Rs.)</label>
                  <input data-testid="principal-input" type="number" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="100000" required />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Rate (%/yr)</label>
                  <input data-testid="rate-input" type="number" step="0.01" value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="8.5" required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Tenure (months)</label>
                  <input data-testid="tenure-input" type="number" value={form.tenure_months} onChange={(e) => setForm({ ...form, tenure_months: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="240" required />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Start Date</label>
                  <input data-testid="start-date-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" required />
                </div>
              </div>
              <button data-testid="submit-emi-btn" type="submit" className="w-full bg-[#1E3F2D] text-white font-semibold rounded-xl px-6 py-3 hover:bg-[#29563E] transition-colors active:scale-95">
                Add Loan
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="loans" className="w-full">
        <TabsList className="bg-[#FBF9F6] border border-[#E8E6E1] rounded-xl p-1">
          <TabsTrigger value="loans" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-loans">My Loans</TabsTrigger>
          <TabsTrigger value="calculator" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-calculator">Calculator</TabsTrigger>
        </TabsList>

        <TabsContent value="loans" className="mt-4 space-y-4">
          {/* Total EMI Card */}
          <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56]">Total Monthly EMI</p>
            <p className="text-3xl font-bold font-mono-numbers text-[#A63C24] mt-1" data-testid="total-emi">{fmt(totalEMI)}</p>
          </div>

          {/* EMI List */}
          <div className="space-y-4" data-testid="emi-list">
            {loading ? (
              <div className="p-8 text-center"><div className="w-6 h-6 border-2 border-[#1E3F2D] border-t-transparent rounded-full animate-spin mx-auto" /></div>
            ) : emis.length === 0 ? (
              <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-8 text-center text-[#5C5A56] text-sm">No loans added yet</div>
            ) : (
              emis.map(emi => (
                <div key={emi.id} className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-5 hover:shadow-md transition-all duration-300" data-testid={`emi-${emi.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-base font-semibold text-[#1C1B1A]">{emi.loan_name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${emi.status === 'active' ? 'bg-[#265C42]/10 text-[#265C42]' : 'bg-[#5C5A56]/10 text-[#5C5A56]'}`}>{emi.status}</span>
                    </div>
                    <button onClick={() => handleDelete(emi.id)} className="text-[#5C5A56] hover:text-[#A63C24] transition-colors p-1">
                      <Trash size={16} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div><p className="text-xs text-[#5C5A56]">Principal</p><p className="text-sm font-semibold font-mono-numbers">{fmt(emi.principal)}</p></div>
                    <div><p className="text-xs text-[#5C5A56]">Monthly EMI</p><p className="text-sm font-semibold font-mono-numbers text-[#A63C24]">{fmt(emi.emi_amount)}</p></div>
                    <div><p className="text-xs text-[#5C5A56]">Total Interest</p><p className="text-sm font-semibold font-mono-numbers text-[#D98324]">{fmt(emi.total_interest)}</p></div>
                    <div><p className="text-xs text-[#5C5A56]">Tenure</p><p className="text-sm font-semibold">{emi.tenure_months} months</p></div>
                  </div>
                  {emi.next_payment_date && (
                    <div className="mt-3 rounded-xl bg-[#FBF9F6] border border-[#E8E6E1] px-3 py-2 text-sm">
                      <span className="text-[#5C5A56]">Next EMI payment date: </span>
                      <span className="font-semibold text-[#1C1B1A]">{emi.next_payment_date}</span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="calculator" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calculator size={20} weight="duotone" className="text-[#1E3F2D]" />
                <h3 className="text-lg font-semibold text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>EMI Calculator</h3>
              </div>
              <form onSubmit={handleCalc} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Loan Amount (Rs.)</label>
                  <input data-testid="calc-principal" type="number" value={calcForm.principal} onChange={(e) => setCalcForm({ ...calcForm, principal: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="500000" required />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Interest Rate (% per year)</label>
                  <input data-testid="calc-rate" type="number" step="0.01" value={calcForm.interest_rate} onChange={(e) => setCalcForm({ ...calcForm, interest_rate: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="8.5" required />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Tenure (months)</label>
                  <input data-testid="calc-tenure" type="number" value={calcForm.tenure_months} onChange={(e) => setCalcForm({ ...calcForm, tenure_months: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="240" required />
                </div>
                <button data-testid="calculate-emi-btn" type="submit" className="w-full bg-[#1E3F2D] text-white font-semibold rounded-xl px-6 py-3 hover:bg-[#29563E] transition-colors active:scale-95">
                  Calculate EMI
                </button>
              </form>
            </div>

            {calcResult && (
              <div className="space-y-4" data-testid="calc-result">
                <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-[#1C1B1A] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Results</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-[#E8E6E1]">
                      <span className="text-sm text-[#5C5A56]">Monthly EMI</span>
                      <span className="text-xl font-bold font-mono-numbers text-[#1E3F2D]" data-testid="calc-emi-result">{fmt(calcResult.emi_amount)}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-[#E8E6E1]">
                      <span className="text-sm text-[#5C5A56]">Total Amount</span>
                      <span className="text-base font-semibold font-mono-numbers">{fmt(calcResult.total_amount)}</span>
                    </div>
                    <div className="flex justify-between items-center py-3">
                      <span className="text-sm text-[#5C5A56]">Total Interest</span>
                      <span className="text-base font-semibold font-mono-numbers text-[#A63C24]">{fmt(calcResult.total_interest)}</span>
                    </div>
                  </div>
                </div>
                {/* Show first 12 months of schedule */}
                <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-6 max-h-64 overflow-y-auto">
                  <h4 className="text-sm font-semibold text-[#1C1B1A] mb-3">Amortization Schedule (first 12 months)</h4>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[#5C5A56] uppercase tracking-wider">
                        <th className="text-left pb-2">#</th>
                        <th className="text-right pb-2">EMI</th>
                        <th className="text-right pb-2">Principal</th>
                        <th className="text-right pb-2">Interest</th>
                        <th className="text-right pb-2">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(calcResult.schedule || []).slice(0, 12).map(s => (
                        <tr key={s.month} className="border-t border-[#E8E6E1]">
                          <td className="py-2">{s.month}</td>
                          <td className="text-right font-mono-numbers">{fmt(s.emi)}</td>
                          <td className="text-right font-mono-numbers text-[#265C42]">{fmt(s.principal)}</td>
                          <td className="text-right font-mono-numbers text-[#A63C24]">{fmt(s.interest)}</td>
                          <td className="text-right font-mono-numbers">{fmt(s.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
